import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { CadObject } from '../../../models/cad-object.model';
import {
  isInterceptorHandledError,
  parseApiError,
} from '../../quotes-api-error';
import { CadFileService } from '../../services/cad-file.service';

@Component({
  selector: 'app-cad-object-tree',
  imports: [ButtonModule, NgTemplateOutlet, Tooltip],
  templateUrl: './cad-object-tree.component.html',
})
export class CadObjectTreeComponent implements OnChanges {
  private readonly cadFileService = inject(CadFileService);
  private readonly messageService = inject(MessageService);

  @Input() cadObjects: CadObject[] = [];
  @Input() primaryCadObjectId: number | null = null;
  @Input() cadFileId: number | null = null;
  @Input() dxfFileUrl: string | null = null;
  @Input() selectable = false;
  @Input() selectedCadObjectId: number | null = null;
  @Input() hoveredCadObjectId: number | null = null;

  @Output() hoveredCadObjectIdChange = new EventEmitter<number | null>();
  @Output() selectedCadObjectChange = new EventEmitter<CadObject | null>();
  // null means every object is visible.
  @Output() visibleCadObjectIdsChange = new EventEmitter<Set<number> | null>();

  readonly downloadingCad = signal(false);
  readonly expandedIds = signal<Set<number>>(new Set());
  private readonly visibleIds = signal<Set<number> | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cadObjects']) {
      this.expandedIds.set(this.collectParentIds(this.cadObjects));
      this.visibleIds.set(null);
      this.visibleCadObjectIdsChange.emit(null);
    }
  }

  isPrimary(cadObject: CadObject): boolean {
    return cadObject.id === this.primaryCadObjectId;
  }

  isSelected(cadObject: CadObject): boolean {
    return this.selectable && cadObject.id === this.selectedCadObjectId;
  }

  selectCadObject(cadObject: CadObject): void {
    if (!this.selectable) {
      return;
    }
    this.selectedCadObjectChange.emit(cadObject);
  }

  isHovered(cadObject: CadObject): boolean {
    return cadObject.id === this.hoveredCadObjectId;
  }

  isVisible(cadObject: CadObject): boolean {
    const visibleIds = this.visibleIds();
    return visibleIds === null || visibleIds.has(cadObject.id);
  }

  toggleVisibility(cadObject: CadObject): void {
    const next = new Set(
      this.visibleIds() ?? this.collectAllIds(this.cadObjects),
    );
    const show = !next.has(cadObject.id);
    for (const id of this.collectAllIds([cadObject])) {
      if (show) {
        next.add(id);
      } else {
        next.delete(id);
      }
    }
    this.visibleIds.set(next);
    this.visibleCadObjectIdsChange.emit(next);
  }

  isExpanded(cadObject: CadObject): boolean {
    return this.expandedIds().has(cadObject.id);
  }

  toggleExpanded(cadObject: CadObject): void {
    this.expandedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(cadObject.id)) {
        next.delete(cadObject.id);
      } else {
        next.add(cadObject.id);
      }
      return next;
    });
  }

  setHoveredCadObject(cadObjectId: number | null): void {
    this.hoveredCadObjectIdChange.emit(cadObjectId);
  }

  async onDownloadCad(): Promise<void> {
    const cadFileId = this.cadFileId;
    if (cadFileId === null || this.downloadingCad()) {
      return;
    }

    this.downloadingCad.set(true);
    try {
      await this.cadFileService.downloadCadFile(cadFileId);
    } catch (error) {
      if (!isInterceptorHandledError(error)) {
        this.messageService.add({
          severity: 'error',
          summary: 'CAD download failed',
          detail: parseApiError(error, 'Failed to download CAD.'),
          life: 8000,
        });
      }
    } finally {
      this.downloadingCad.set(false);
    }
  }

  private collectParentIds(cadObjects: CadObject[]): Set<number> {
    const parentIds = new Set<number>();
    for (const cadObject of cadObjects) {
      if (cadObject.children?.length) {
        parentIds.add(cadObject.id);
        for (const childId of this.collectParentIds(cadObject.children)) {
          parentIds.add(childId);
        }
      }
    }
    return parentIds;
  }

  private collectAllIds(cadObjects: CadObject[]): Set<number> {
    const ids = new Set<number>();
    for (const cadObject of cadObjects) {
      ids.add(cadObject.id);
      for (const childId of this.collectAllIds(cadObject.children ?? [])) {
        ids.add(childId);
      }
    }
    return ids;
  }
}
